import React, { useCallback, useState } from 'react';

import { chatService } from '../../services/chatService';
import { ActionBarActions } from '../AiOverviewPanel/ActionBarActions';
import { recordUserActionClick } from '../AiOverviewPanel/useActionBarClicks';

import './DevPlanButtons.css';
import {
  DeveloperPlanEnvelope,
  DeveloperPlanState,
} from './developerPlan';
import { DevPlanExplainModal } from './DevPlanExplainModal';

interface DevPlanButtonsProps {
  envelope: DeveloperPlanEnvelope;
  /** Latest known state for this plan id — may have been updated by a later
   *  state/result envelope in the same session. Falls back to envelope.state. */
  liveState: DeveloperPlanState | undefined;
  /** Project the plan belongs to (the API path needs it). */
  projectId: string;
  /** Session the plan belongs to. */
  sessionId: string;
  /** Chat message that supplied this developer-plan decision. */
  entryId: string;
  /** AI Overview can render these controls as a whole bar or inside the shared action group. */
  variant?: DevPlanButtonVariant;
}

type Pending = 'approve' | 'deny' | null;
type ButtonTone = 'explain' | 'approve' | 'deny';
type DevPlanButtonVariant = 'inline' | 'actionBar' | 'actionGroup';

const ExplainIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M4 5.5A2.5 2.5 0 0 1 6.5 3h11A2.5 2.5 0 0 1 20 5.5v9A2.5 2.5 0 0 1 17.5 17h-11A2.5 2.5 0 0 1 4 14.5v-9Z" stroke="currentColor" strokeWidth="2" />
    <path d="m10 8 4 2-4 2V8Z" fill="currentColor" />
    <path d="M9 21h6M12 17v4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const ApproveIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const DenyIcon: React.FC = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
    <path d="m18 6-12 12M6 6l12 12" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
  </svg>
);

const Spinner: React.FC<{ variant: DevPlanButtonVariant }> = ({ variant }) => (
  <span className={variant !== 'inline' ? 'ai-overview-action-spinner' : 'dev-plan-btn__spinner'} aria-hidden="true" />
);

const buttonClassName = (tone: ButtonTone, variant: DevPlanButtonVariant): string => {
  if (variant === 'inline') return `dev-plan-btn dev-plan-btn--${tone}`;
  if (tone === 'approve') return 'ai-overview-action-btn ai-overview-action-btn--continue dev-plan-action-control';
  if (tone === 'deny') return 'ai-overview-action-btn ai-overview-action-btn--reject-srd dev-plan-action-control';

  return 'ai-overview-action-btn ai-overview-action-btn--explain dev-plan-action-control';
};

const actionCountClass = (count: number) => (
  count > 0 && count <= 6 ? ` ai-overview-action-bar__actions--count-${count}` : ''
);

const ActionFrame: React.FC<{
  variant: DevPlanButtonVariant;
  count: number;
  resolved?: boolean;
  error?: string | null;
  children: React.ReactNode;
}> = ({ variant, count, resolved = false, error, children }) => {
  if (variant === 'actionGroup') {
    return (
      <>
        {children}
        {error && <span className="dev-plan-actions__error dev-plan-actions__error--action-group" role="alert">{error}</span>}
      </>
    );
  }

  if (variant === 'actionBar') {
    return (
      <div className="ai-overview-action-bar dev-plan-action-bar">
        <ActionBarActions className={actionCountClass(count)}>
          {children}
        </ActionBarActions>
        {error && <span className="dev-plan-actions__error dev-plan-actions__error--action-bar" role="alert">{error}</span>}
      </div>
    );
  }

  return (
    <div className={`dev-plan-actions${resolved ? ' dev-plan-actions--resolved' : ''}`}>
      {children}
      {error && <span className="dev-plan-actions__error" role="alert">{error}</span>}
    </div>
  );
};

interface ExplainBtnProps {
  onOpen: () => void;
  disabled?: boolean;
  variant: DevPlanButtonVariant;
}

const ExplainBtn: React.FC<ExplainBtnProps> = ({ onOpen, disabled, variant }) => (
  <button
    type="button"
    className={buttonClassName('explain', variant)}
    onClick={onOpen}
    disabled={disabled}
  >
    <ExplainIcon />
    <span>Explain</span>
  </button>
);

interface PendingActionsProps {
  pending: Pending;
  onApprove: () => void;
  onDeny: () => void;
  variant: DevPlanButtonVariant;
}

const PendingActions: React.FC<PendingActionsProps> = ({
  pending, onApprove, onDeny, variant,
}) => (
  <>
    <button
      type="button"
      className={buttonClassName('approve', variant)}
      onClick={onApprove}
      disabled={!!pending}
    >
      {pending === 'approve' ? <Spinner variant={variant} /> : <ApproveIcon />}
      <span>{pending === 'approve' ? 'Approving…' : 'Approve plan'}</span>
    </button>
    <button
      type="button"
      className={buttonClassName('deny', variant)}
      onClick={onDeny}
      disabled={!!pending}
    >
      {pending === 'deny' ? <Spinner variant={variant} /> : <DenyIcon />}
      <span>{pending === 'deny' ? 'Denying…' : 'Deny'}</span>
    </button>
  </>
);

interface ResolvedViewProps {
  state: DeveloperPlanState;
  presentationHtml: string;
  showExplain: boolean;
  onOpenExplain: () => void;
  onCloseExplain: () => void;
  variant: DevPlanButtonVariant;
}

const ResolvedView: React.FC<ResolvedViewProps> = ({
  state, presentationHtml, showExplain, onOpenExplain, onCloseExplain, variant,
}) => (
  <ActionFrame variant={variant} count={presentationHtml ? 2 : 1} resolved>
    <span className={`dev-plan-status dev-plan-status--${state}`}>
      Plan {state.replace(/[_:-]+/g, ' ')}
    </span>
    {presentationHtml && <ExplainBtn onOpen={onOpenExplain} variant={variant} />}
    {showExplain && (
      <DevPlanExplainModal presentationHtml={presentationHtml} onClose={onCloseExplain} />
    )}
  </ActionFrame>
);

const errorMessage = (err: unknown, fallback: string): string => (
  err instanceof Error ? err.message : fallback
);

export const getDevPlanActionButtonCount = (
  envelope: DeveloperPlanEnvelope,
  liveState: DeveloperPlanState | undefined,
): number => {
  const state = liveState ?? envelope.state;
  const hasPresentation = !!envelope.presentation_html;

  if (state && state !== 'pending_approval') return hasPresentation ? 2 : 1;

  return (hasPresentation ? 1 : 0) + 2;
};

// eslint-disable-next-line max-lines-per-function
const usePlanActions = (
  projectId: string, sessionId: string, entryId: string, planId: string,
) => {
  const [pending, setPending] = useState<Pending>(null);
  const [error, setError] = useState<string | null>(null);

  // eslint-disable-next-line max-statements
  const run = useCallback(async (
    action: 'approve' | 'deny',
    buttonKey: 'approve_plan' | 'deny_plan',
    displayedLabel: string,
    fn: () => Promise<void>,
    fallbackError: string,
  ) => {
    if (pending) return;
    setError(null);
    setPending(action);
    await recordUserActionClick(
      entryId,
      'developer_plan',
      buttonKey,
      displayedLabel,
      projectId,
      sessionId,
    );
    try {
      await fn();
    } catch (err) {
      setError(errorMessage(err, fallbackError));
      setPending(null);
    }
    // Keep pending set until the backend pushes a new state envelope;
    // the buttons unmount on the next render because liveState changes.
  }, [entryId, pending, projectId, sessionId]);

  const approve = useCallback(() => run(
    'approve',
    'approve_plan',
    'Approve plan',
    () => chatService.approveDeveloperPlan(projectId, sessionId, planId),
    'Failed to approve plan',
  ), [run, projectId, sessionId, planId]);

  const deny = useCallback(() => run(
    'deny',
    'deny_plan',
    'Deny',
    () => chatService.denyDeveloperPlan(projectId, sessionId, planId),
    'Failed to deny plan',
  ), [run, projectId, sessionId, planId]);

  return { pending, error, approve, deny };
};

// eslint-disable-next-line max-lines-per-function, max-statements
export const DevPlanButtons: React.FC<DevPlanButtonsProps> = ({
  envelope, liveState, projectId, sessionId, entryId, variant = 'inline',
}) => {
  const [showExplain, setShowExplain] = useState(false);
  const state = liveState ?? envelope.state;
  const presentationHtml = envelope.presentation_html ?? '';
  const { pending, error, approve, deny } = usePlanActions(
    projectId, sessionId, entryId, envelope.plan_id,
  );

  const openExplain = useCallback(() => {
    void recordUserActionClick(
      entryId, 'developer_plan', 'explain_plan', 'Explain', projectId, sessionId,
    );
    setShowExplain(true);
  }, [entryId, projectId, sessionId]);
  const closeExplain = useCallback(() => setShowExplain(false), []);

  if (state && state !== 'pending_approval') {
    return (
      <ResolvedView
        state={state}
        presentationHtml={presentationHtml}
        showExplain={showExplain}
        onOpenExplain={openExplain}
        onCloseExplain={closeExplain}
        variant={variant}
      />
    );
  }

  const actionCount = (presentationHtml ? 1 : 0) + 2;

  return (
    <ActionFrame variant={variant} count={actionCount} error={error}>
      {presentationHtml && <ExplainBtn onOpen={openExplain} disabled={!!pending} variant={variant} />}
      <PendingActions pending={pending} onApprove={approve} onDeny={deny} variant={variant} />
      {showExplain && (
        <DevPlanExplainModal
          presentationHtml={presentationHtml}
          onClose={closeExplain}
          showPlanActions
          pending={pending}
          error={error}
          onApprove={approve}
          onDeny={deny}
        />
      )}
    </ActionFrame>
  );
};
